import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Send, Sparkles, CheckCircle2, AlertTriangle,
          MessageSquare, RotateCw, Trophy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SIM = `${API}/academy/simulator`;

/**
 * SimulatorChat — AI Client roleplay for objection lessons in Module 7.
 *
 * Flow:
 *   1. Component starts a session (POST /start), gets AI opening line.
 *   2. Trainee replies → POST /{id}/reply → AI returns next line.
 *   3. After 2+ trainee turns, "Завершить симуляцию" grades with Gemini.
 *   4. Verdict has 3-part breakdown (empathy/value/close) and XP.
 */
export default function SimulatorChat({ scenarioId, onCompleted, onFinished }) {
  const [session, setSession] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${SIM}/start`,
        { scenario_id: scenarioId }, { withCredentials: true });
      setSession(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось запустить AI-клиента");
    } finally { setBusy(false); }
  };

  useEffect(() => { start(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scenarioId]);

  useEffect(() => {
    // scroll bottom when new messages arrive
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session?.turns?.length]);

  const send = async () => {
    if (!text.trim() || !session || busy) return;
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${SIM}/${session.id}/reply`,
        { text: text.trim() }, { withCredentials: true });
      setSession(r.data);
      setText("");
    } catch (e) {
      setError(e.response?.data?.detail || "AI не ответил, повторите");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    if (!session || busy) return;
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${SIM}/${session.id}/finish`, {},
        { withCredentials: true });
      setSession(r.data);
      if (r.data.verdict?.passed) onCompleted?.();
      // Fire always (pass or fail) so callers driving the final exam can
      // upload the session_id for aggregate scoring.
      onFinished?.(r.data.id || session.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось оценить симуляцию");
    } finally { setBusy(false); }
  };

  const restart = async () => {
    setSession(null);
    await start();
  };

  if (!session && busy) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Loader2 size={20} className="animate-spin"
          style={{ color: "#C5A059", display: "inline-block" }} />
        <div style={{ marginTop: 8, fontSize: 12, color: "#8a8a89" }}>
          Запускаем AI-клиента…
        </div>
      </div>
    );
  }
  if (!session) {
    return (
      <div style={{ padding: 20, color: "#7f1d1d", fontSize: 13 }}>
        {error || "Ошибка запуска"}
      </div>
    );
  }

  const trainee_turn_count = (session.turns || [])
    .filter(t => t.role === "trainee").length;
  const canFinish = trainee_turn_count >= 2 && session.status === "open";
  const graded = session.status === "graded";

  return (
    <div data-testid="simulator-chat"
      style={{ marginTop: 20, background: "white", borderRadius: 12,
                border: "1px solid #efeee9", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", background: "#fafaf9",
                     borderBottom: "1px solid #efeee9",
                     display: "flex", alignItems: "center", gap: 8 }}>
        <MessageSquare size={16} color="#C5A059" />
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 18,
                       color: "#0A0A0A", flex: 1 }}>
          AI-клиент: {session.scenario_label}
        </div>
        <div style={{ fontSize: 10, textTransform: "uppercase",
                       letterSpacing: 0.5, color: "#8a8a89", fontWeight: 700 }}>
          Ход {trainee_turn_count}
        </div>
      </div>

      <div ref={scrollRef}
        style={{ maxHeight: 380, overflowY: "auto", padding: 16,
                  background: "#fafaf9" }}>
        {(session.turns || []).map((t, i) => (
          <div key={i}
            style={{ display: "flex",
                      justifyContent: t.role === "trainee" ? "flex-end" : "flex-start",
                      marginBottom: 8 }}>
            <div data-testid={`sim-msg-${t.role}-${i}`}
              style={{
                maxWidth: "78%",
                padding: "8px 12px",
                background: t.role === "trainee"
                  ? "linear-gradient(135deg,#C5A059,#D4B878)" : "white",
                color: t.role === "trainee" ? "#0A0A0A" : "#0A0A0A",
                borderRadius: 10,
                border: t.role === "trainee" ? "none" : "1px solid #efeee9",
                fontFamily: "Manrope",
                fontSize: 13.5,
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
              }}>
              {t.text}
            </div>
          </div>
        ))}
        {busy && !graded && (
          <div style={{ display: "flex", alignItems: "center", gap: 6,
                        color: "#8a8a89", fontSize: 12 }}>
            <Loader2 size={12} className="animate-spin" /> клиент печатает…
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: "#fef2f2",
                       borderTop: "1px solid #fecaca",
                       color: "#7f1d1d", fontSize: 13 }}>
          {error}
        </div>
      )}

      {graded && session.verdict ? (
        <VerdictCard verdict={session.verdict} xp={session.xp_earned}
                       onRestart={restart} />
      ) : (
        <div style={{ padding: 12, borderTop: "1px solid #efeee9",
                       background: "white",
                       display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea data-testid="sim-input"
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault(); send();
            }}}
            placeholder="Ваш ответ клиенту… (Enter — отправить)"
            rows={2}
            style={{ flex: 1, resize: "none",
                      border: "1px solid #e2e0d7", borderRadius: 6,
                      padding: "8px 10px", fontFamily: "Manrope",
                      fontSize: 13, outline: "none", color: "#0A0A0A" }} />
          <button data-testid="sim-send" onClick={send} disabled={busy || !text.trim()}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", border: "none", borderRadius: 6,
                      padding: "0 14px", cursor: busy ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                      fontWeight: 700, fontSize: 12, minHeight: 44,
                      opacity: (busy || !text.trim()) ? 0.5 : 1 }}>
            <Send size={13} /> Отправить
          </button>
        </div>
      )}

      {canFinish && !graded && (
        <div style={{ padding: 10, borderTop: "1px solid #efeee9",
                       background: "#fafaf9",
                       display: "flex", justifyContent: "space-between",
                       alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 11, color: "#8a8a89" }}>
            Провели минимум 2 хода — можно завершить и получить оценку AI.
          </div>
          <button data-testid="sim-finish" onClick={finish} disabled={busy}
            style={{ background: "white", border: "1px solid #C5A059",
                      color: "#0A0A0A", borderRadius: 6, padding: "8px 14px",
                      cursor: "pointer", fontFamily: "Manrope",
                      fontWeight: 700, fontSize: 12,
                      display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Sparkles size={12} /> Завершить и оценить
          </button>
        </div>
      )}
    </div>
  );
}

function VerdictCard({ verdict, xp, onRestart }) {
  const passed = verdict.passed;
  return (
    <div data-testid="sim-verdict"
      style={{ padding: 18,
                background: passed ? "#ecfdf5" : "#fffbea",
                borderTop: `1px solid ${passed ? "#a7f3d0" : "#fde68a"}` }}>
      <div style={{ display: "flex", alignItems: "center",
                     gap: 10, marginBottom: 10 }}>
        {passed ? <CheckCircle2 size={22} color="#059669" />
                : <AlertTriangle size={22} color="#B45309" />}
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22,
                       color: passed ? "#065f46" : "#78350f" }}>
          {verdict.score}/{verdict.max} · {passed ? "Отлично!" : "Попробуйте ещё"}
        </div>
        {xp > 0 && (
          <div style={{ marginLeft: "auto", fontSize: 12, color: "#059669",
                          fontWeight: 700 }}>
            +{xp} XP
          </div>
        )}
      </div>
      {verdict.breakdown && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12,
                       flexWrap: "wrap" }}>
          {[
            ["empathy", "Услышал клиента"],
            ["value", "Раскрыл ценность"],
            ["close", "Довёл до следующего шага"],
          ].map(([k, label]) => (
            <div key={k} style={{ background: "white", padding: "6px 10px",
                                     borderRadius: 6, fontSize: 12,
                                     border: "1px solid #e2e0d7" }}>
              {label}: <b>{verdict.breakdown[k]}/10</b>
            </div>
          ))}
        </div>
      )}
      {verdict.text && (
        <div style={{ padding: 10, background: "white", borderRadius: 6,
                       border: "1px solid #e2e0d7", fontSize: 13,
                       color: "#0A0A0A", marginBottom: 12 }}>
          <b>Комментарий AI-тренера:</b> {verdict.text}
        </div>
      )}
      {verdict.achievement && verdict.achievement !== "none" && (
        <div data-testid="sim-achievement"
          style={{ padding: 10, background: "#fef3c7",
                    border: "1px solid #fde68a", borderRadius: 6,
                    fontSize: 13, color: "#78350f", marginBottom: 12,
                    display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Trophy size={14} />
          Новое достижение: <b>{verdict.achievement === "objection_master"
            ? "Мастер возражений" : "Closer"}</b>
        </div>
      )}
      <button data-testid="sim-restart" onClick={onRestart}
        style={{ background: "white", border: "1px solid #d1d5db",
                  borderRadius: 6, padding: "8px 14px", cursor: "pointer",
                  fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6 }}>
        <RotateCw size={12} /> Попробовать другой сценарий
      </button>
    </div>
  );
}
